export interface QueuedAction {
  id: string
  type: "rsvp" | "check-in"
  url: string
  method: string
  headers: Record<string, string>
  body: string
  queuedAt: number
}

const DB_NAME = "creatix-offline-queue"
const STORE_NAME = "pending-actions"
const DB_VERSION = 1

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "id" })
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

function withStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, mode)
        const store = tx.objectStore(STORE_NAME)
        const req = fn(store)
        req.onsuccess = () => resolve(req.result)
        req.onerror = () => reject(req.error)
      })
  )
}

async function registerSync(type: QueuedAction["type"]) {
  if (!("serviceWorker" in navigator)) return
  try {
    const reg = await navigator.serviceWorker.ready
    const sync = reg as ServiceWorkerRegistration & {
      sync?: { register: (tag: string) => Promise<void> }
    }
    await sync.sync?.register(`${type}-sync`)
  } catch {
    // Background Sync not supported (e.g. iOS Safari)
  }
}

export const offlineQueue = {
  add: async (action: Omit<QueuedAction, "id" | "queuedAt">) => {
    const record: QueuedAction = {
      ...action,
      id: crypto.randomUUID(),
      queuedAt: Date.now(),
    }
    await withStore("readwrite", (store) => store.add(record))
    await registerSync(action.type)
    return record
  },

  getAll: async (): Promise<QueuedAction[]> => {
    return withStore("readonly", (store) => store.getAll())
  },

  remove: async (id: string) => {
    await withStore("readwrite", (store) => store.delete(id))
  },

  count: async (): Promise<number> => {
    const all = await offlineQueue.getAll()
    return all.length
  },
}

export async function replayQueuedActionsOnLoad() {
  if (!navigator.onLine) return

  const actions = await offlineQueue.getAll()
  for (const action of actions) {
    try {
      const response = await fetch(action.url, {
        method: action.method,
        headers: action.headers,
        body: action.body,
      })
      if (response.ok) {
        await offlineQueue.remove(action.id)
        window.dispatchEvent(new CustomEvent("offline-action-synced", { detail: action }))
      }
    } catch {
      // Still offline or network error — keep in queue
    }
  }
}
